/**
 * API client for the Beconfident community gate.
 *
 * Paths and shapes for the OTP endpoints are sourced from @beconfident/api-lib
 * via **type-only** imports. The runtime zod schemas and path strings are
 * declared locally because api-lib's compiled output requires @zodios/core,
 * which pulls in axios - that's ~15 kB of bundle we don't need here since
 * the gate uses fetch.
 *
 * Local schemas are annotated with `: z.ZodType<api-lib-type>` so that a
 * contract drift in api-lib will fail typecheck. Drop the locals and the
 * annotations once api-lib publishes a fetch-based / zero-runtime-dep bundle.
 *
 * The /join endpoint is not in api-lib yet - search TODO(api-lib).
 */
import { z } from "zod";
import type { sendOTP as sendOTPDef } from "@beconfident/api-lib/src/auth/sendOTP";
import type { validateOTP as validateOTPDef } from "@beconfident/api-lib/src/auth/validateOTP";
import type {
  EventDetail,
  EventMeta,
  EventStatus,
  GateErrorCode,
  JoinErrorCode,
  JoinSuccess,
} from "@/types/gate";
import { getToken } from "./jwt";

const BASE_URL = (import.meta.env.VITE_API_URL ?? "").replace(/\/+$/, "");

export type SendOtpResponse = z.infer<typeof sendOTPDef.response>;
export type ValidateOtpResponse = z.infer<typeof validateOTPDef.response>;

export class ApiError extends Error {
  readonly status: number;
  readonly code: GateErrorCode | null;
  readonly retryAfterSeconds: number | null;
  readonly event: EventMeta | null;

  constructor(
    message: string,
    params: {
      status: number;
      code?: GateErrorCode | null;
      retryAfterSeconds?: number | null;
      event?: EventMeta | null;
    },
  ) {
    super(message);
    this.name = "ApiError";
    this.status = params.status;
    this.code = params.code ?? null;
    this.retryAfterSeconds = params.retryAfterSeconds ?? null;
    this.event = params.event ?? null;
  }
}

// ---------------------------------------------------------------------------
// Paths + runtime schemas.
//
// Each local schema is annotated with the api-lib type so that a backend
// contract change surfaced through api-lib will break our typecheck.
// ---------------------------------------------------------------------------

const OTP_SEND_PATH = "/app/auth/otp/send";
const OTP_VALIDATE_PATH = "/app/auth/otp/validate";

const sendOtpResponseSchema: z.ZodType<SendOtpResponse> = z.object({
  success: z.boolean(),
});

const validateOtpResponseSchema: z.ZodType<ValidateOtpResponse> = z.object({
  token: z.string(),
});

const eventStatusSchema: z.ZodType<EventStatus> = z.enum([
  "published",
  "live",
  "finished",
  "cancelled",
  "interrupted",
  "participants_synced",
]);

// ---------------------------------------------------------------------------
// Schemas for /join - local until api-lib ships this endpoint.
// TODO(api-lib): replace with the library export when available.
// ---------------------------------------------------------------------------

const eventMetaSchema: z.ZodType<EventMeta> = z.object({
  title: z.string(),
  scheduledAt: z.string(),
  status: eventStatusSchema,
});

const joinSuccessSchema = z
  .object({
    accessLink: z.string().url(),
    attendanceKind: z.enum(["confirmed", "walked_in"]),
    event: eventMetaSchema.optional(),
  })
  .transform(
    (raw): JoinSuccess =>
      raw.event === undefined
        ? { accessLink: raw.accessLink, attendanceKind: raw.attendanceKind }
        : {
            accessLink: raw.accessLink,
            attendanceKind: raw.attendanceKind,
            event: raw.event,
          },
  );

const eventHostSchema = z.object({
  hostId: z.string(),
  name: z.string(),
  title: z.string().default(""),
  imageUrl: z.string().url().nullish(),
  nationality: z.string().default(""),
});

const eventLevelSchema = z.object({
  code: z.string(),
  label: z.string().default(""),
});

const eventAccentSchema = z.object({
  code: z.string(),
  label: z.string(),
  flag: z.string(),
});

const eventDetailSchema = z
  .object({
    eventId: z.string(),
    title: z.string(),
    description: z.string().optional(),
    coverImageUrl: z.string().url().nullish(),
    scheduledAt: z.string(),
    estimatedDuration: z.number().nullish(),
    status: eventStatusSchema,
    isFull: z.boolean(),
    requirements: z.array(z.string()).optional(),
    hosts: z.array(eventHostSchema).optional(),
    levels: z.array(eventLevelSchema).optional(),
    accent: eventAccentSchema.nullish(),
    userAttendanceStatus: z.string().nullish(),
  })
  .transform(
    (raw): EventDetail => ({
      eventId: raw.eventId,
      title: raw.title,
      description: raw.description ?? "",
      coverImageUrl: raw.coverImageUrl ?? null,
      scheduledAt: raw.scheduledAt,
      estimatedDuration: raw.estimatedDuration ?? null,
      status: raw.status,
      isFull: raw.isFull,
      requirements: raw.requirements ?? [],
      hosts: (raw.hosts ?? []).map((h) => ({ ...h, imageUrl: h.imageUrl ?? null })),
      levels: raw.levels ?? [],
      accent: raw.accent ?? null,
      userAttendanceStatus: raw.userAttendanceStatus ?? null,
    }),
  );

const joinErrorCodes: readonly JoinErrorCode[] = [
  "not_confirmed",
  "event_full",
  "not_live_yet",
  "event_ended",
  "event_cancelled",
];

const joinErrorSchema = z.object({
  error: z.enum(["not_confirmed", "event_full", "not_live_yet", "event_ended", "event_cancelled"]),
  event: eventMetaSchema.optional(),
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parseRetryAfter(header: string | null): number | null {
  if (!header) return null;
  const numeric = Number.parseInt(header, 10);
  if (Number.isFinite(numeric) && numeric >= 0) return numeric;
  return null;
}

interface RequestOptions {
  method: "GET" | "POST";
  body?: unknown;
  auth?: boolean;
  signal?: AbortSignal;
}

async function request(path: string, options: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  if (options.auth) {
    const token = getToken();
    if (!token) {
      throw new ApiError("missing auth token", { status: 401 });
    }
    headers["Authorization"] = `Bearer ${token}`;
  }

  try {
    const init: RequestInit = {
      method: options.method,
      headers,
      credentials: "omit",
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    };
    return await fetch(`${BASE_URL}${path}`, init);
  } catch {
    throw new ApiError("network error", { status: 0, code: "network" });
  }
}

async function readJson<T extends z.ZodTypeAny>(
  response: Response,
  schema: T,
): Promise<z.output<T>> {
  const text = await response.text();
  let parsed: unknown;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    throw new ApiError("invalid json", { status: response.status, code: "network" });
  }
  const result = schema.safeParse(parsed);
  if (!result.success) {
    throw new ApiError("invalid response shape", {
      status: response.status,
      code: "network",
    });
  }
  return result.data;
}

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

export async function sendOtp(whatsapp: string): Promise<void> {
  const response = await request(OTP_SEND_PATH, {
    method: "POST",
    body: { whatsapp },
  });
  if (response.status === 429) {
    throw new ApiError("rate limited", {
      status: 429,
      code: "rate_limited",
      retryAfterSeconds: parseRetryAfter(response.headers.get("Retry-After")),
    });
  }
  if (!response.ok) {
    throw new ApiError("failed to send otp", {
      status: response.status,
      code: "network",
    });
  }
  const parsed = await readJson(response, sendOtpResponseSchema);
  if (!parsed.success) {
    throw new ApiError("send otp reported failure", { status: 200, code: "network" });
  }
}

export async function validateOtp(whatsapp: string, code: string): Promise<string> {
  const response = await request(OTP_VALIDATE_PATH, {
    method: "POST",
    body: { whatsapp, code },
  });
  if (response.status === 400 || response.status === 401) {
    throw new ApiError("invalid code", { status: response.status, code: "otp_invalid" });
  }
  if (response.status === 429) {
    throw new ApiError("rate limited", {
      status: 429,
      code: "rate_limited",
      retryAfterSeconds: parseRetryAfter(response.headers.get("Retry-After")),
    });
  }
  if (!response.ok) {
    // The backend's OTPCodeHandler.validate throws "user not found" when
    // there is no Beconfident user matching the WhatsApp number — Express
    // surfaces this as a 500 with the message in the body. Treat it as a
    // typed account_not_found so the gate can show a sign-up CTA instead
    // of a generic network error.
    const body = await response.text().catch(() => "");
    if (response.status >= 500 && /user.*not.*found/i.test(body)) {
      throw new ApiError("account not found", {
        status: response.status,
        code: "account_not_found",
      });
    }
    throw new ApiError("failed to validate otp", {
      status: response.status,
      code: "network",
    });
  }
  const parsed = await readJson(response, validateOtpResponseSchema);
  return parsed.token;
}

/**
 * POST /app/community/events/:eventId/join
 *
 * TODO(api-lib): this endpoint is planned but not yet exported by api-lib. The
 * schemas above are a local projection of what the backend is expected to
 * return. Replace with the library's endpoint definition once published.
 */
export async function joinEvent(eventId: string): Promise<JoinSuccess> {
  const response = await request(`/app/community/events/${encodeURIComponent(eventId)}/join`, {
    method: "POST",
    auth: true,
  });

  if (response.status === 409 || response.status === 410) {
    const body = await readJson(response, joinErrorSchema).catch(() => null);
    const code: JoinErrorCode =
      body && joinErrorCodes.includes(body.error)
        ? body.error
        : response.status === 410
          ? "event_ended"
          : "not_confirmed";
    throw new ApiError("join conflict", {
      status: response.status,
      code,
      event: body?.event ?? null,
    });
  }

  if (response.status === 429) {
    throw new ApiError("rate limited", {
      status: 429,
      code: "rate_limited",
      retryAfterSeconds: parseRetryAfter(response.headers.get("Retry-After")),
    });
  }

  if (response.status === 400) {
    // The backend throws a 400 BadRequest with "Cannot set capacity to N:
    // there are already M attendees" when a walk-in collides with an
    // admin-side capacity sync. Signal a capacity-race so the UI refetches
    // the event instead of surfacing a generic network error.
    const body = await response.text();
    if (/capacity/i.test(body)) {
      throw new ApiError("capacity race", { status: 400, code: "capacity_race" });
    }
    throw new ApiError("bad request", { status: 400, code: "network" });
  }

  if (!response.ok) {
    throw new ApiError("failed to join", {
      status: response.status,
      code: "network",
    });
  }

  return readJson(response, joinSuccessSchema);
}

/**
 * GET /app/community/events/:eventId
 *
 * Auth required. Backend returns a large event shape; we project only the
 * fields the preview screen renders.
 *
 * TODO(api-lib): replace with the library's endpoint when available.
 */
export async function getEventDetail(eventId: string): Promise<EventDetail> {
  const response = await request(`/app/community/events/${encodeURIComponent(eventId)}`, {
    method: "GET",
    auth: true,
  });

  if (response.status === 404) {
    throw new ApiError("event not found", { status: 404, code: "event_not_found" });
  }
  if (response.status === 429) {
    throw new ApiError("rate limited", {
      status: 429,
      code: "rate_limited",
      retryAfterSeconds: parseRetryAfter(response.headers.get("Retry-After")),
    });
  }
  if (response.status === 401) {
    throw new ApiError("unauthorized", { status: 401, code: "network" });
  }
  if (!response.ok) {
    throw new ApiError("failed to fetch event", {
      status: response.status,
      code: "network",
    });
  }

  return readJson(response, eventDetailSchema);
}

// Projection of api-lib's `licenseSchema` (libs/api-lib/src/core/license.ts),
// narrowed to the two fields the paid-check needs. `status` stays a loose
// string rather than the four-value enum on purpose: a status we don't know yet
// should read as "not active", not blow up the response parse and drop the user
// on an error screen.
const meLicenseSchema = z.object({
  status: z.string(),
  endDate: z.string().nullish(),
});

/**
 * A user is paid when they hold an active license. This mirrors the backend's
 * own definition — see the query in
 * apps/server/src/scripts/fixes/resyncLegacyPurchaseStatusForActiveLicenses.ts:
 * `status: "active"` with `endDate` either absent or in the future.
 *
 * An `endDate` we can't parse counts as active on purpose. The failure mode
 * worth avoiding is paywalling someone who paid.
 */
function isActiveLicense(license: z.infer<typeof meLicenseSchema>): boolean {
  if (license.status !== "active") return false;
  if (!license.endDate) return true;
  const endsAt = Date.parse(license.endDate);
  return Number.isNaN(endsAt) || endsAt > Date.now();
}

const meResponseSchema = z
  .object({
    _id: z.string(),
    licenses: z.array(meLicenseSchema).nullish(),
  })
  .transform((raw) => ({
    userId: raw._id,
    isPaid: (raw.licenses ?? []).some((license) => isActiveLicense(license)),
  }));

export type MeResponse = z.output<typeof meResponseSchema>;

/**
 * GET /app/me
 *
 * Serves two purposes after authentication: it gates on the user's
 * subscription state, and it resolves the Beconfident user id that the gate
 * binds to its telemetry so participation can be attributed to a person.
 *
 * Paid status reads `licenses`, not `purchase.status`. The legacy flag is a
 * shim the backend still writes on every license activation
 * (apps/server/src/application/licenses/licenseService.ts, search for the TODO
 * about removing it), and it goes stale for licences activated outside that
 * path — TECH-1665 had to backfill exactly that population. A stale flag here
 * means paywalling a subscriber out of an event they paid for.
 */
export async function getMe(): Promise<MeResponse> {
  const response = await request("/app/me", { method: "GET", auth: true });

  if (response.status === 401) {
    throw new ApiError("unauthorized", { status: 401, code: "network" });
  }
  if (response.status === 429) {
    throw new ApiError("rate limited", {
      status: 429,
      code: "rate_limited",
      retryAfterSeconds: parseRetryAfter(response.headers.get("Retry-After")),
    });
  }
  if (!response.ok) {
    throw new ApiError("failed to fetch profile", {
      status: response.status,
      code: "network",
    });
  }

  return readJson(response, meResponseSchema);
}
